import { nameOfSide, sideOwning } from '@polf/shared/match'
import { useState } from 'react'

import {
  type CurrentPlayerEventMatchesQuery,
  HoleWinnerEnum,
  MatchSideEnum,
  MatchStatusEnum,
} from '../../__generated__/hooks'
import { decidedMargin, runningStanding } from '../../util/match-progression'
import MatchHoleWinner from '../MatchHoleWinner'
import DetailSheet from './DetailSheet'

type Match = CurrentPlayerEventMatchesQuery['currentPlayerEventMatches'][number]

/**
 * The match leaderboard: results, not a ranking.
 *
 * A single round of match play has no honest aggregate standing — everyone
 * plays one match, so a points table is a column of 1s and 0s with nothing to
 * separate ties. The results ARE the leaderboard, ordered by match number so
 * position never shifts under the reader (design.md §8.1).
 *
 * Your own match is highlighted in place rather than pulled to the top.
 */
const MatchView = ({
  matches,
  youId,
}: {
  matches: Match[]
  youId?: string
}) => {
  // Tapping a row opens how that match played out — the same progression the
  // scorecard shows for your own pairing, in the sheet the other leaderboard
  // variants already use for a row tap (POL-80).
  const [openId, setOpenId] = useState<string | null>(null)
  const open = matches.find((m) => m.id === openId) ?? null
  if (matches.length === 0) {
    return (
      <section className="px-4 py-8 text-center text-ink-500">
        Ingen matcher ennå.
      </section>
    )
  }

  return (
    <div className="flex flex-col gap-2 px-2 pb-6">
      {matches.map((match) => {
        const { state } = match
        // Your TEAM's match is yours too: in a team event the entrant is the
        // team, so neither named side carries your own name.
        const isYours = sideOwning(match, youId) !== null
        const nameFor = (side: MatchSideEnum) => nameOfSide(match, side)

        const decided = state?.status === MatchStatusEnum.Decided
        const halved = state?.status === MatchStatusEnum.Halved
        const dormie = state?.status === MatchStatusEnum.Dormie

        let result = 'All square'
        let tone = 'text-ink-700'
        if (decided && state) {
          result = `${nameFor(state.leader ?? MatchSideEnum.A)} ${decidedMargin(
            state
          )}`.trim()
          tone = 'text-gold'
        } else if (halved) {
          result = 'Delt'
        } else if (state?.leader && state.holesUp > 0) {
          result = `${nameFor(state.leader)} ${state.holesUp} UP`
          tone = 'text-green-700'
        }

        return (
          <button
            key={match.id}
            type="button"
            onClick={() => setOpenId(match.id)}
            className={`w-full text-left rounded-lg px-3 py-2.5 flex items-center gap-3 ${
              isYours
                ? 'bg-green-50 border-l-[3px] border-green-700'
                : 'bg-paper'
            }`}
          >
            <span className="font-mono num text-xs text-ink-500 w-5 shrink-0">
              {match.matchNumber}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-ink-900 truncate">
                {nameFor(MatchSideEnum.A)} mot {nameFor(MatchSideEnum.B)}
              </div>
              <div className={`text-xs font-bold ${tone}`}>{result}</div>
            </div>
            {!decided && !halved && state ? (
              <span className="font-mono num text-xs text-ink-500 shrink-0">
                {dormie ? 'Dormie · ' : ''}
                {state.holesRemaining} hull igjen
              </span>
            ) : null}
          </button>
        )
      })}

      {open ? (
        <MatchDetail match={open} onClose={() => setOpenId(null)} />
      ) : null}
    </div>
  )
}

/** How one match played out, hole by hole. */
const MatchDetail = ({
  match,
  onClose,
}: {
  match: Match
  onClose: () => void
}) => {
  const { state } = match
  const holes = (state?.holes ?? []) as HoleWinnerEnum[]
  const standings = runningStanding(state?.holeSwings ?? [])
  const leaderName = nameOfSide(match, state?.leader)

  return (
    <DetailSheet
      open
      onClose={onClose}
      title={`${nameOfSide(match, MatchSideEnum.A)} mot ${nameOfSide(
        match,
        MatchSideEnum.B
      )}`}
      subtitle={`Match ${match.matchNumber}`}
      primary={
        state?.margin
          ? `${leaderName ?? ''} ${decidedMargin(state)}`.trim()
          : standings[standings.length - 1] ?? 'AS'
      }
      secondary={
        state && state.status !== MatchStatusEnum.Decided
          ? `${state.holesRemaining} hull igjen`
          : null
      }
    >
      {holes.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-ink-500">
          Ingen hull spilt ennå.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-caps text-ink-500">
              <th className="text-left font-bold py-1 px-3">Hull</th>
              <th className="text-center font-bold py-1">Vinner</th>
              <th className="text-right font-bold py-1 px-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {holes.map((_, i) => {
              return (
                <tr
                  // Position in the played sequence is the identity — a
                  // shotgun flight plays holes out of number order.
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  className="border-t border-ink-100/60"
                >
                  <td className="py-1.5 px-3 font-mono num text-ink-700">
                    {i + 1}
                  </td>
                  <td className="py-1.5 text-center text-ink-900">
                    <MatchHoleWinner
                      legs={state?.holeLegs?.[i] ?? []}
                      nameFor={(side) => nameOfSide(match, side)}
                    />
                  </td>
                  <td className="py-1.5 px-3 text-right font-mono num font-bold text-ink-900">
                    {standings[i]}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </DetailSheet>
  )
}

export default MatchView
